import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import dayjs from 'dayjs';
import { uniqBy } from 'lodash';

import API from '@api/API';

import { useTopEntertainersQuery } from '@queries/EntertainerQueries';

import { useCreateScheduleDateDetailsEventMutation } from '@mutations/UserScheduleSubmissionsMutations';

import Button from '@components/buttons/Button';
import { DatePicker } from '@components/form/DatePicker';
import { FormLabel } from '@components/form/FormLabel';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Select } from '@components/form/Select';
import Card from '@components/layout/Card/Card';
import { Popup } from '@components/layout/Popup/Popup';

import { EntertainerScheduleDateDetail } from '@screens/entertainer/DragKingCommunityScreen/ScheduledSocialPost';
import { MultiSelectField } from '@screens/entertainer/components/CitiesField';

interface CreateEntertainerScheduleEventMutationPayload {
  date: string;
  title: string;
  startDate: string;
  startTime: string;
  cityId: string;
  entertainerIds: string[];
  tagIds: string[];
}

const useCreateEntertainerScheduleEventMutation = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({
      entertainerId,
      eventImage,
      ...payload
    }: CreateEntertainerScheduleEventMutationPayload & {
      entertainerId: string;
      eventImage?: string | ArrayBuffer;
    }) => {
      const { event } = await API.post<
        { event: { _id: string } },
        CreateEntertainerScheduleEventMutationPayload
      >(`/v1/entertainer-schedules/${entertainerId}/events`, payload);

      if (eventImage) {
        await API.put(`/v1/events/${event._id}`, { eventImage });
      }

      return { event };
    },
  });

  return {
    createEntertainerScheduleEventAsync: mutateAsync,
    isCreatingEntertainerScheduleEvent: isPending,
  };
};

interface CreateEntertainerScheduleEventPopupContentProps {
  entertainerScheduleDateDetail: EntertainerScheduleDateDetail;
  seriesId?: string;
  cityId: string | undefined;
  cityOptions: { label: string; value: string }[];
  entertainerType?: 'dragPerformer' | 'dj' | 'comedian';
  onClose: () => void;
  onCreated?: () => void;
}

/**
 * Form for creating a new event for an entertainer schedule date based on the entertainer schedule date detail,
 * Copy from CreateEditEventPopup
 *
 * Fields:
 * - Flyer Image
 * - Show Name
 * - Show Date/Time
 * - Entertainers, initialized with the current entertainer
 */
const CreateEntertainerScheduleEventPopupContent = ({
  entertainerScheduleDateDetail,
  seriesId,
  cityId: initialCityId,
  cityOptions = [],
  entertainerType = 'dragPerformer',
  onClose,
  onCreated,
}: CreateEntertainerScheduleEventPopupContentProps) => {
  const { entertainer, date, dateDetails } = entertainerScheduleDateDetail;
  const cityId = initialCityId ?? cityOptions?.[0]?.value ?? '';
  const [updatedEventProperties, setUpdatedEventProperties] = useState<{
    title?: string;
    startDate?: string;
    startTime?: string;
    cityId?: string;
    entertainerIds?: string[];
    eventImage?: string | ArrayBuffer;
  }>({
    title: dateDetails.showName,
    startDate: date,
    startTime: dateDetails.startTime,
    cityId,
    entertainerIds: [entertainer._id],
  });
  const [error, setError] = useState<string | undefined>();
  const { createScheduleDateDetailsEventAsync, isSubmitting: isCreatingScheduleDateDetailsEvent } =
    useCreateScheduleDateDetailsEventMutation();

  const { topEntertainers = [] } = useTopEntertainersQuery(cityId, entertainerType, true);

  const entertainerOptions = useMemo(
    () =>
      uniqBy([entertainer, ...topEntertainers], '_id')
        .map(({ _id, title }) => ({ value: _id, label: title ?? _id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [entertainer, topEntertainers],
  );

  const startDateTime = useMemo(
    () =>
      `${updatedEventProperties.startDate ?? ''} ${updatedEventProperties.startTime ?? ''}`.trim(),
    [updatedEventProperties.startDate, updatedEventProperties.startTime],
  );

  const canSubmit = Boolean(
    !isCreatingScheduleDateDetailsEvent &&
    updatedEventProperties.title &&
    updatedEventProperties.startDate &&
    updatedEventProperties.startTime &&
    updatedEventProperties.cityId,
  );

  const handleCreateEvent = async () => {
    if (!canSubmit) {
      setError('Title, start date/time, and city are required.');
      return;
    }
    setError(undefined);
    await createScheduleDateDetailsEventAsync({
      scheduleDateDetailsId: dateDetails._id,
      title: updatedEventProperties.title!,
      startDate: updatedEventProperties.startDate!,
      startTime: updatedEventProperties.startTime!,
      cityId: updatedEventProperties.cityId!,
      eventImage: updatedEventProperties.eventImage,
      entertainerIds: updatedEventProperties.entertainerIds ?? [entertainer._id],
      tagIds: seriesId ? [seriesId] : [],
    });
    onCreated?.();
    onClose();
  };

  return (
    <div>
      <Card>
        <ImageUploadField
          placeholder="Add an event flyer..."
          defaultValue={dateDetails.img}
          onChange={(updatedEventImage) =>
            setUpdatedEventProperties((prevValue) => ({
              ...prevValue,
              eventImage: updatedEventImage,
            }))
          }
        />
      </Card>
      <div className="d-grid gap-2 mt-2">
        <div>
          <FormLabel text="Title *" />
          <Input
            name="title"
            defaultValue={updatedEventProperties.title}
            onChange={(e) =>
              setUpdatedEventProperties((prevValue) => ({ ...prevValue, title: e.target.value }))
            }
          />
        </div>

        <div>
          <FormLabel text="Start *" />
          <DatePicker
            showTimeSelect
            name="startDateTime"
            value={startDateTime}
            onChange={(updatedDate) =>
              setUpdatedEventProperties((prevValue) => ({
                ...prevValue,
                startDate: dayjs(updatedDate).format('YYYY-MM-DD'),
                startTime: dayjs(updatedDate).format('HH:mm'),
              }))
            }
          />
        </div>

        <div>
          <FormLabel text="City *" />
          <Select
            name="cityId"
            options={cityOptions}
            value={updatedEventProperties.cityId}
            onChange={(e) =>
              setUpdatedEventProperties((prevValue) => ({ ...prevValue, cityId: e.target.value }))
            }
          />
        </div>

        <div>
          <FormLabel text="Entertainers *" />
          <MultiSelectField
            name="entertainerIds"
            allowSearch
            defaultValue={updatedEventProperties.entertainerIds ?? []}
            options={entertainerOptions}
            onChange={(updatedEntertainerIds) =>
              setUpdatedEventProperties((prevValue) => ({
                ...prevValue,
                entertainerIds: updatedEntertainerIds,
              }))
            }
          />
        </div>
      </div>

      {error && <p className="text-danger mt-1 mb-0">{error}</p>}

      <Button className="mt-3" disabled={!canSubmit} onClick={handleCreateEvent}>
        Create Event
      </Button>
    </div>
  );
};

interface CreateEntertainerScheduleEventPopupProps extends Omit<
  CreateEntertainerScheduleEventPopupContentProps,
  'entertainerScheduleDateDetail'
> {
  isVisible: boolean;
  entertainerScheduleDateDetail?: EntertainerScheduleDateDetail;
}

export const CreateEntertainerScheduleEventPopup = ({
  isVisible,
  entertainerScheduleDateDetail,
  ...contentProps
}: CreateEntertainerScheduleEventPopupProps) => {
  return (
    <Popup isVisible={isVisible} onClose={contentProps.onClose} height="full">
      {isVisible && entertainerScheduleDateDetail && (
        <Popup.Content>
          <CreateEntertainerScheduleEventPopupContent
            entertainerScheduleDateDetail={entertainerScheduleDateDetail}
            {...contentProps}
          />
        </Popup.Content>
      )}
    </Popup>
  );
};
